import { SyntheticEvent, useEffect, useMemo, useReducer, useRef } from 'react'

import { faMapLocationDot } from '@fortawesome/pro-regular-svg-icons'
import { faFilter } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Badge,
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import {
  DataGridPro,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridColumnMenuContainer,
  GridColumnMenuProps,
  GridColumnPinningMenuItems,
  GridPinnedColumns,
  GridSortModel,
  SortGridMenuItems,
  useGridApiContext,
  useGridApiRef,
} from '@mui/x-data-grid-pro'
import { ColumnsManagementDialog } from '@scoir/data-table'
import { Tooltip } from '@scoir/tooltips'
import { Link } from 'react-router-dom'

import useTitle from 'common/use-title'
import FourOhFour from 'routes/status/FourOhFour'
import { useAppSelector } from 'store'
import { useCurrentSeniorYear } from 'store/current-senior-year/hooks'
import { hasPermission } from 'store/user'

import HighschoolPreviewDrawer from '../Preview/HighschoolPreviewDrawer'
import FilterDrawer from '../components/FilterDrawer'
import PremiumActionsWrapper from '../components/PremiumPreview/PremiumActions'
import SearchTextField from '../components/TableComponents/SearchTextField'
import { useTableHeightListener } from '../components/TableComponents/useTableHeightListener'
import ViewsButton from '../components/Views/ViewsButton'
import { useViews } from '../components/Views/useViews'
import getFilterCount from '../filter-state-count'
import { createAPIFiltersFromUIFilters } from '../filter-state-to-api-query'
import { encodeExploreMapState } from '../filter-state-to-map-url-params'
import ColumnManagement from './ColumnManagement'
import ExploreToolbar from './ExploreToolbar'
import ExportButton from './ExportButton'
import getColumns, { columnSectionOrderModel, getColumnVisibility } from './columns'
import {
  Class,
  FOLLOWER_COLUMN_FIELD,
  HANDRAISER_COLUMN_FIELD,
  HISTORIC_FOLLOWER_COLUMN_FIELD,
  HistoricFollowerYear,
  getFollowerClasses,
  getHandraiserClasses,
  getHistoricFollowerYears,
} from './gradYears'
import highSchoolExploreReducer from './state'
import { PAGE_SIZE_OPTIONS, PageSizeOption, SortKey } from './types'
import useGetHighschoolsExplore from './useGetHighschoolsExplore'
import useTargetHighschoolsExplore from './useTargetHighschoolsExplore'

export default function ExploreTable({ testOnlyDisableVirtualization }: { testOnlyDisableVirtualization?: boolean }) {
  useTitle('Scoir - High Schools')
  const canView = useAppSelector((state) =>
    hasPermission(state, 'PERFORM_VISIT_MANAGEMENT_TASKS_FOR_ASSOCIATED_COLLEGE'),
  )
  const [tableHeight, ref] = useTableHeightListener()
  const {
    activeView,
    currentPage,
    pageSize,
    updateColumnVisibility,
    updateColumnOrder,
    changeActiveView,
    updatePinnedColumns,
    updateColumnFilters,
    updateColumnSort,
    updateColumnWidth,
    updatePageSize,
    updateCurrentPage,
    updateColumnConfig,
  } = useViews()

  const activeViewRef = useRef(activeView)
  const displayedTableView = activeViewRef.current

  const [state, dispatch] = useReducer(highSchoolExploreReducer, {
    selectedSchools: [],
    isFilterDrawerOpen: false,
    isViewChanging: false,
    columnManagementOpen: false,
  })
  const isPremiumExplorerEnabled = useAppSelector(
    (state) => state.college.record?.Features?.SupportsPremiumHighSchoolExplorer || false,
  )
  const selectedHandraiserClasses = getHandraiserClasses(activeView.columnConfigModel)
  const setSelectedHandraiserClasses = (next: Class[]) => updateColumnConfig(HANDRAISER_COLUMN_FIELD, { classes: next })

  const selectedFollowerClasses = getFollowerClasses(activeView.columnConfigModel)
  const setSelectedFollowerClasses = (next: Class[]) => updateColumnConfig(FOLLOWER_COLUMN_FIELD, { classes: next })

  const selectedHistoricFollowerYears = getHistoricFollowerYears(activeView.columnConfigModel)
  const setSelectedHistoricFollowerYears = (next: HistoricFollowerYear[]) =>
    updateColumnConfig(HISTORIC_FOLLOWER_COLUMN_FIELD, { years: next })

  const modelSortKey = activeView.sortModel?.field as SortKey | undefined
  const getHighschoolExplore = useGetHighschoolsExplore({
    resultOptions: {
      from: currentPage * pageSize || 0,
      limit: pageSize || 25,
      sortKey: modelSortKey || 'name',
      sortDirection: activeView.sortModel?.sort || 'desc',
    },
    filters: createAPIFiltersFromUIFilters(activeView.filterModel, {
      handraiserClasses: [...selectedHandraiserClasses],
      followerClasses: [...selectedFollowerClasses],
      historicFollowerYears: [...selectedHistoricFollowerYears],
    }),
    enabled: canView,
    viewId: activeView.id,
  })

  const currentSeniorYear = useCurrentSeniorYear()

  useEffect(() => {
    if (activeView.id !== displayedTableView.id) {
      dispatch({ type: 'view-changing-toggled', isChanging: true })
    }
  }, [activeView.id, displayedTableView.id])

  useEffect(() => {
    if (activeView.id === displayedTableView.id) {
      activeViewRef.current = activeView
      dispatch({ type: 'view-changing-toggled', isChanging: false })
    } else if (state.isViewChanging && !getHighschoolExplore.isLoading) {
      activeViewRef.current = activeView
      dispatch({ type: 'view-changing-toggled', isChanging: false })
    }
  }, [activeView, getHighschoolExplore.isLoading, state.isViewChanging, displayedTableView.id])

  const { toggleTargeted } = useTargetHighschoolsExplore(getHighschoolExplore.applyTargets)

  const columns = getColumns({
    onClickPreview: (id) => {
      dispatch({ type: 'preview-highschool-change', previewHighschoolId: id })
    },
    userDisplay: displayedTableView,
    toggleTargeted,
    selectedHandraiserClasses,
    onChangeHandraiserClasses: setSelectedHandraiserClasses,
    selectedFollowerClasses,
    onChangeFollowerClasses: setSelectedFollowerClasses,
    selectedHistoricFollowerYears,
    onChangeHistoricFollowerYears: setSelectedHistoricFollowerYears,
    currentSeniorYear,
    showPremiumColumns: isPremiumExplorerEnabled,
  })

  const highschools = useMemo(() => getHighschoolExplore?.data?.highSchools || [], [getHighschoolExplore])
  const totalPages = !getHighschoolExplore?.data?.total ? 1 : getHighschoolExplore?.data?.total / pageSize

  const previewHighschoolIndex = state.previewHighschoolId
    ? highschools.findIndex((r) => r.id === state.previewHighschoolId)
    : 0

  // Restores previewHighschoolId when next/previous buttons cross a pagination boundary. Stored in ref so it can update without a re-render which is important when used in `useEffect`.
  const requestedPreviewHighschoolIndex = useRef<number | undefined | null>()

  const previewHighSchool = highschools?.[previewHighschoolIndex]

  const hasNextPreview = previewHighschoolIndex < highschools.length - 1 || currentPage < totalPages - 1

  const hasPrevPreview = previewHighschoolIndex > 0 || currentPage > 0

  const handleClickNextRecord = (highschoolId?: string) => {
    if (highschools.length === 0 || highschoolId === undefined) {
      return
    }

    const nextIndex = previewHighschoolIndex + 1
    if (nextIndex === highschools.length && currentPage < totalPages - 1) {
      updateCurrentPage(currentPage + 1)
      requestedPreviewHighschoolIndex.current = 0
      return
    }
    dispatch({
      type: 'preview-highschool-change',
      previewHighschoolId: highschools[nextIndex].id,
    })
  }

  const handleClickPreviousRecord = (highschoolId?: string) => {
    if (highschools.length === 0 || highschoolId === undefined) {
      return
    }

    const previousIndex = previewHighschoolIndex - 1
    if (previousIndex < 0 && currentPage > 0) {
      updateCurrentPage(currentPage - 1)
      requestedPreviewHighschoolIndex.current = pageSize - 1
      return
    }
    dispatch({
      type: 'preview-highschool-change',
      previewHighschoolId: highschools[previousIndex].id,
    })
  }

  useEffect(() => {
    if (!getHighschoolExplore.isLoading && requestedPreviewHighschoolIndex?.current != undefined) {
      dispatch({
        type: 'preview-highschool-change',
        previewHighschoolId: highschools[requestedPreviewHighschoolIndex.current].id,
      })
    }
  }, [getHighschoolExplore.isLoading, highschools])

  // Need to use a ref & subscribe to the drag event to prevent from constantly firing off
  // updates to the order model.
  const apiRef = useGridApiRef()
  useEffect(() => {
    const handleColumnHeaderDragEnd = () => {
      const gridOrderState = apiRef.current.exportState()?.columns?.orderedFields
      if (gridOrderState) {
        const newOrderModel = gridOrderState.reduce(
          (acc, field, index) => ({
            ...acc,
            [field]: index,
          }),
          {},
        )
        updateColumnOrder(newOrderModel)
      }
    }

    const api = apiRef.current
    if (!api?.subscribeEvent) {
      return
    }

    const unsubscribe = api.subscribeEvent('columnHeaderDragEnd', handleColumnHeaderDragEnd)
    return () => {
      unsubscribe?.()
    }
  }, [apiRef, displayedTableView.orderModel, updateColumnOrder])

  const handlePinnedColumnsChange = (pinnedModel: GridPinnedColumns) => {
    updatePinnedColumns(pinnedModel)
  }

  const filterCount = getFilterCount(activeView.filterModel)

  if (!canView) {
    return <FourOhFour />
  }
  if (!displayedTableView) return null

  return (
    <Box>
      <Paper elevation={0}>
        <Snackbar
          open={getHighschoolExplore.isError}
          message="There was an error getting high schools."
        />
        <Box
          display="flex"
          flexWrap="wrap"
          gridGap={24}
          alignItems="center"
          justifyContent="space-between"
          p={2}
        >
          <Box
            display="flex"
            gridGap={8}
            minWidth={state.selectedSchools.length ? 600 : 400}
          >
            <SearchTextField
              defaultValue={activeView.filterModel.text}
              onChange={(val) => {
                updateColumnFilters({ ...activeView.filterModel, text: val || undefined })
              }}
            />
            <ExploreToolbar
              selectedSchools={state.selectedSchools}
              schoolData={getHighschoolExplore?.data?.highSchools}
              toggleTargeted={toggleTargeted}
            />
          </Box>
          <Box
            display="flex"
            gridGap={16}
          >
            <Badge
              color="primary"
              badgeContent={filterCount}
            >
              <Typography variant="srOnly">{filterCount} filters applied</Typography>
              <ResponsiveFilterButton onClick={() => dispatch({ type: 'filter-drawer-toggled', isOpen: true })} />
            </Badge>
            <FilterDrawer
              defaultFilters={activeView.filterModel}
              isOpen={state.isFilterDrawerOpen}
              onClose={() => {
                dispatch({ type: 'filter-drawer-toggled', isOpen: false })
              }}
              onClickApply={(f) => {
                updateColumnFilters(f)
                dispatch({ type: 'filter-drawer-toggled', isOpen: false })
              }}
              showPremiumFilters={isPremiumExplorerEnabled}
              selectedHandraiserClasses={selectedHandraiserClasses}
              selectedFollowerClasses={selectedFollowerClasses}
              selectedHistoricFollowerYears={selectedHistoricFollowerYears}
            />
            <PremiumActionsWrapper
              enabled={isPremiumExplorerEnabled}
              trackingIdPlacement="column-management"
            >
              <ColumnManagement
                enabled={isPremiumExplorerEnabled}
                onOpenColumnsManagement={() => dispatch({ type: 'column-management-toggled', isOpen: true })}
              />
            </PremiumActionsWrapper>
            <PremiumActionsWrapper
              enabled={isPremiumExplorerEnabled}
              trackingIdPlacement="saved-views"
            >
              <ViewsButton enabled={isPremiumExplorerEnabled} />
            </PremiumActionsWrapper>
            <PremiumActionsWrapper
              enabled={isPremiumExplorerEnabled}
              trackingIdPlacement="export"
            >
              <ExportButton
                enabled={isPremiumExplorerEnabled}
                columns={columns}
                resultOptions={{
                  from: 0,
                  limit: getHighschoolExplore.data?.maxExportLimit || 0,
                  sortKey: (displayedTableView.sortModel?.field as SortKey) || 'name',
                  sortDirection: displayedTableView.sortModel?.sort || 'asc',
                }}
                filters={activeView.filterModel}
                selectedHandraiserClasses={selectedHandraiserClasses}
                selectedFollowerClasses={selectedFollowerClasses}
                selectedHistoricFollowerYears={selectedHistoricFollowerYears}
              />
            </PremiumActionsWrapper>
            <ResponsiveViewInMapButton
              to={`./highschools/map?${encodeExploreMapState({
                ...activeView.filterModel,
                lat: getHighschoolExplore.data?.center?.lat,
                lng: getHighschoolExplore.data?.center?.lng,
                zoom: 6,
              }).toString()}`}
              disabled={
                getHighschoolExplore.isLoading ||
                getHighschoolExplore.data?.total === 0 ||
                !getHighschoolExplore.data?.center
              }
            />
          </Box>
        </Box>
        <DataGridPro
          ref={ref}
          sx={{
            '&.MuiDataGrid-root': {
              height: `calc(100vh - ${tableHeight}px)`,
            },
          }}
          apiRef={apiRef}
          columnVisibilityModel={getColumnVisibility(displayedTableView.visibilityModel, isPremiumExplorerEnabled)}
          onColumnVisibilityModelChange={updateColumnVisibility}
          onSortModelChange={(model: GridSortModel) => {
            updateColumnSort(model[0])
          }}
          sortModel={(() => {
            const sortField = displayedTableView.sortModel?.field
            const fieldExists = sortField && columns.some((c) => c.field === sortField)
            return [
              {
                field: fieldExists ? sortField : 'name',
                sort: displayedTableView.sortModel?.sort,
              },
            ]
          })()}
          disableColumnReorder={isPremiumExplorerEnabled ? false : true}
          columns={columns}
          onColumnWidthChange={(params) => {
            updateColumnWidth({
              ...displayedTableView.widthModel,
              [params.colDef.field]: Math.ceil(params.colDef.computedWidth),
            })
          }}
          disableVirtualization={testOnlyDisableVirtualization}
          rows={getHighschoolExplore.data?.highSchools || []}
          sortingOrder={['desc', 'asc']}
          rowCount={getHighschoolExplore.data?.total || 0}
          loading={getHighschoolExplore.isLoading}
          disableColumnFilter
          disableColumnMenu={isPremiumExplorerEnabled ? false : true}
          pagination
          paginationMode="server"
          rowsPerPageOptions={[...PAGE_SIZE_OPTIONS]}
          page={currentPage}
          onPageChange={(page) => {
            updateCurrentPage(page)
          }}
          pinnedColumns={
            displayedTableView.pinnedModel
              ? {
                  ...displayedTableView.pinnedModel,
                  left: [
                    GRID_CHECKBOX_SELECTION_COL_DEF.field,
                    ...(displayedTableView.pinnedModel.left?.filter(
                      (col) => col !== GRID_CHECKBOX_SELECTION_COL_DEF.field,
                    ) || []),
                  ],
                }
              : { left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, 'name', 'target'] }
          }
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            updatePageSize(size as PageSizeOption)
          }}
          sortingMode="server"
          checkboxSelection
          selectionModel={state.selectedSchools}
          onSelectionModelChange={(newSelection) => {
            dispatch({ type: 'selection-change', selectedSchools: newSelection.map(String) })
          }}
          onPinnedColumnsChange={handlePinnedColumnsChange}
          components={{ ColumnMenu: CustomColumnMenu }}
        />
        <ColumnsManagementDialog
          open={state.columnManagementOpen}
          onClose={() => dispatch({ type: 'column-management-toggled', isOpen: false })}
          setColumns={(v) => {
            const definedCols = v.filter((c) => c?.field)
            changeActiveView({
              ...displayedTableView,
              visibilityModel: definedCols.reduce((acc, col) => ({ ...acc, [col.field]: col.isVisible ?? false }), {}),
              orderModel: definedCols.reduce((acc, col, index) => ({ ...acc, [col.field]: index }), {}),
            })
          }}
          setColumnVisibilityModel={(newModel) => {
            updateColumnVisibility(newModel)
          }}
          columnVisibilityModel={displayedTableView?.visibilityModel || {}}
          columns={columns}
          columnSectionOrderModel={columnSectionOrderModel}
        />
      </Paper>
      <HighschoolPreviewDrawer
        open={!!state.previewHighschoolId}
        dataIsLoading={getHighschoolExplore.isLoading}
        onClickNext={handleClickNextRecord}
        onClickPrev={handleClickPreviousRecord}
        hasNext={hasNextPreview}
        hasPrevious={hasPrevPreview}
        highschool={previewHighSchool}
        toggleTargeted={toggleTargeted}
        onClose={() => {
          dispatch({ type: 'preview-highschool-change', previewHighschoolId: null })
          requestedPreviewHighschoolIndex.current = null
        }}
      />
    </Box>
  )
}

function CustomColumnMenu(props: GridColumnMenuProps) {
  const { hideMenu, currentColumn, ...other } = props
  const apiRef = useGridApiContext()

  const hideColumn = (event: SyntheticEvent) => {
    apiRef.current.setColumnVisibility(currentColumn.field, false)
    hideMenu(event)
  }

  return (
    <GridColumnMenuContainer
      hideMenu={hideMenu}
      currentColumn={currentColumn}
      {...other}
    >
      <GridColumnPinningMenuItems
        onClick={hideMenu}
        column={currentColumn}
      />
      <SortGridMenuItems
        onClick={hideMenu}
        column={currentColumn}
      />
      <MenuItem onClick={hideColumn}>
        <Typography>Remove</Typography>
      </MenuItem>
    </GridColumnMenuContainer>
  )
}

const ResponsiveFilterButton = ({ onClick }: { onClick: () => void }) => {
  const theme = useTheme()
  const isMediumScreenSize = useMediaQuery(theme.breakpoints.down('md'))
  return isMediumScreenSize ? (
    <Tooltip title="Filters">
      <IconButton
        color="primary"
        onClick={onClick}
      >
        <FontAwesomeIcon icon={faFilter} />
      </IconButton>
    </Tooltip>
  ) : (
    <Button
      color="primary"
      onClick={onClick}
      startIcon={<FontAwesomeIcon icon={faFilter} />}
    >
      Filters
    </Button>
  )
}

const ResponsiveViewInMapButton = ({ to, disabled }: { to: string; disabled: boolean }) => {
  const theme = useTheme()
  const isMediumScreenSize = useMediaQuery(theme.breakpoints.down('sm'))
  return isMediumScreenSize ? (
    <Tooltip title="View in Map">
      <IconButton
        component={Link}
        to={to}
        color="primary"
        disabled={disabled}
      >
        <FontAwesomeIcon icon={faMapLocationDot} />
      </IconButton>
    </Tooltip>
  ) : (
    <Button
      component={Link}
      to={to}
      variant="outlined"
      color="primary"
      disabled={disabled}
      startIcon={<FontAwesomeIcon icon={faMapLocationDot} />}
    >
      View in Map
    </Button>
  )
}
